import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { empty1040Form, IRS1040Form, Uploaded1040Document } from "../../model/tax/irs1040.d";
import { IRSSchedule1Form, UploadedSchedule1Document, UploadedSchedule1DocumentCollection } from "../../model/tax/irsschedule1.d";
import { DocumentOwner, IRSW2Form, UploadedW2Document, UploadedW2DocumentCollection } from "../../model/tax/irsw2.d";
import {  DocumentOrError, TaxDocument, TaxDocumentError, UploadedFile } from "../../model/tax/tax_document.d";
import { TaxReturn } from "../../model/tax/tax_return.d";
import { omitDollar } from "../../utils/dataConverter";
import { detectMissingLocalWithholdings } from "../../utils/detectLocalWithholdings";
import { isMobile } from "react-device-detect";

import {
  FormTypes,
  KeyValuePayload,
} from "../../utils/models";
import { ResourceTask, taskReducers, TaskState } from "../../utils/tasks";
import { IRSScheduleCForm, UploadedScheduleCDocument, UploadedScheduleCDocumentCollection } from "../../model/tax/irsschedulec";
import { ConfirmW2Params } from "../model/tax";
import { knownBadLocalities } from "../saga/TaxSaga";

export interface TaxState extends TaskState {
  tasks: ResourceTask[];
  tax_return: TaxReturn | null;
  irs1040: TaxDocument<IRS1040Form> | TaxDocumentError | null;
  t1040: Uploaded1040Document | null;
  irsW2Forms: DocumentOrError<IRSW2Form>[];
  uploadedW2s: UploadedW2Document[] | null;
  irsSchedule1Forms: (TaxDocument<IRSSchedule1Form> | TaxDocumentError)[];
  uploadedSchedule1s: UploadedSchedule1Document[] | null;
  irsScheduleCForms: (TaxDocument<IRSScheduleCForm> | TaxDocumentError)[];
  uploadedScheduleCs: UploadedScheduleCDocument[] | null;
  editMode: { [index: number]: boolean };
  errorMessage: string;
  failureCount: number;
  isRetiredOrDisabled: boolean;
  show1040NoIncomePrompt: boolean;
  showLocalWithholdingsUploadPrompt: boolean;
  showMissingBox18Prompt: boolean;
  showLocalWithholdingsPrompt: boolean;
  showInvalidBox5Prompt: boolean;
  showInvalidLocalityPrompt: boolean;
  selectedFormIndex: number;
}

const initialState: TaxState = {
  tasks: [],
  tax_return: null,
  irs1040: null,
  t1040: null,
  irsW2Forms: [],
  uploadedW2s: null,
  irsSchedule1Forms: [],
  uploadedSchedule1s: null,
  irsScheduleCForms: [],
  uploadedScheduleCs: null,
  editMode: {},
  errorMessage: "",
  failureCount: 0,
  isRetiredOrDisabled: false,
  show1040NoIncomePrompt: false,
  showLocalWithholdingsUploadPrompt: false,
  showMissingBox18Prompt: false,
  showLocalWithholdingsPrompt: false,
  showInvalidBox5Prompt: false,
  showInvalidLocalityPrompt: false,
  selectedFormIndex: -1
};

export const taxSlice = createSlice({
  name: "tax",
  initialState,
  reducers: {
    ...taskReducers,
    fetchTaxReturnRequest: (_) => {},
    setTaxReturn: (state, action: PayloadAction<TaxReturn>) => {
      state.tax_return = action.payload;
    },
    addW2Message: (state, action: PayloadAction<string>) => {
      state.tax_return!.IRSW2.messages ??= [];
      state.tax_return!.IRSW2.messages.push(action.payload);
    },
    setEditMode: (state, action: PayloadAction<{ [index: number]: boolean }>) => {
      state.editMode = action.payload;
    },
    toggleEditMode: (state, action: PayloadAction<{index: number, value: boolean}>) => {
      state.editMode[action.payload.index] = action.payload.value;
    },
    selectForm: (state, action: PayloadAction<number>) => {
      state.selectedFormIndex = action.payload;
    },
    toggleShowLocalWithholdingsUploadPrompt: (state, action: PayloadAction<boolean>) => {
      state.showLocalWithholdingsUploadPrompt = action.payload;
    },
    toggleShowLocalWithholdingsPrompt: (state, action: PayloadAction<boolean | undefined>) => {
      state.showLocalWithholdingsPrompt = action?.payload ?? !state.showLocalWithholdingsPrompt;
    },
    toggleShowInvalidBox5Prompt: (state, action: PayloadAction<boolean>) => {
      state.showInvalidBox5Prompt = action.payload;
    },
    toggleShowInvalidLocalityPrompt: (state, action: PayloadAction<boolean>) => {
      state.showInvalidLocalityPrompt = action.payload;
    },
    toggleShowMissingBox18Prompt: (state, action: PayloadAction<boolean>) => {
      state.showMissingBox18Prompt = action.payload;
    },
    addLocalTax: (state, action: PayloadAction<number>) => {
      const w2 = state.irsW2Forms[action.payload] as TaxDocument<IRSW2Form>;
      w2.form_data.local_withholdings = [
        ...(w2.form_data.local_withholdings ?? []),
        {
          local_income_tax: 0,
          local_wages_and_tips: 0,
          locality_name: ""
        }]
    },
    deleteLocalTax: (state, action: PayloadAction<{w2Idx: number, transIdx: number}>) => {
      const w2 = state.irsW2Forms[action.payload.w2Idx] as TaxDocument<IRSW2Form>;
      // delete at index
      w2.form_data.local_withholdings!.splice(action.payload.transIdx, 1);
    },
    fetch1040FormDetails: (_) => {},
    fetch1040FormDetailsSuccess: (
      state,
      action: PayloadAction<Uploaded1040Document>
    ) => {
      state.t1040 = action.payload;
      state.irs1040 = {
        document_name: FormTypes.t1040,
        uploaded_file: action.payload.tax_document,
        /// TODO: Stop hard-coding this when the backend returns it
        resource_uri: "/forms/IRS1040",
        form_data: action.payload.tax_document.form_data,
        warnings: {},
      }
    },

    fetch1040FormDetailsError: (_) => {},
    upload1040Request: (state, action: PayloadAction<File>) => {
      state.errorMessage = "";
    },
    upload1040Error: (state, action: PayloadAction<string>) => {
      state.irs1040 = { document_name: "error", message: action.payload };
      state.failureCount += 1;
    },
    upload1040Success: (
      state,
      action: PayloadAction<TaxDocument<IRS1040Form>>
    ) => {
      state.irs1040 = action.payload;
      state.failureCount = 0;
    },
    delete1040Success: (state) => {
      state.irs1040 = null;
      state.t1040 = empty1040Form;
      state.isRetiredOrDisabled = false;
    },
    update1040ValueChange: (
      state,
      action: PayloadAction<KeyValuePayload>
    ) => {
      const _irs1040 = state.irs1040 as TaxDocument<IRS1040Form>;
      state.irs1040 = {
        ..._irs1040,
        form_data: {
          ..._irs1040.form_data,
          [action.payload.key]: action.payload.value,
        },
      };
      const return1040 = state.tax_return?.IRS1040;
      if (return1040) {
        return1040.messages = null;
      }
    },
    validate1040Income: (_) => {},
    proceedToNextStep: (_, action: PayloadAction<{form: FormTypes, balance: boolean}>) => {},
    show1040NoIncomePrompt: (state, action: PayloadAction<boolean>) => {
      state.show1040NoIncomePrompt = action.payload;
    },
    confirm1040Request: (_) => {},
    delete1040Request: (_) => {},
    delete1040Error: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    fetchW2FormDetails: (_) => {},
    fetchW2FormDetailsSuccess: (
      state,
      action: PayloadAction<UploadedW2DocumentCollection>
    ) => {
      state.uploadedW2s = action.payload.tax_document.map((w2) => {
        return {
          id: w2.id as number,
          tax_document: {
            id: w2.id,
            file_name: w2.file_name,
            file_ulid: w2.file_ulid,
            form_data: {
              employer_name: w2.employer_name,
              employer_address: w2.employer_address,
              local_withholdings: w2.local_withholdings,
              wages_tips_other_comp_box1: w2.wages_tips_other_comp_box1,
              medicare_wages_and_tips: w2.medicare_wages_and_tips,
              employer_tax_id: String(w2.employer_tax_id)?.substring(0, 2) + '-' + String(w2.employer_tax_id)?.substring(2),
              document_owner: w2.document_owner,
            },
          }
        }
      });
      state.irsW2Forms = [
        ...state.irsW2Forms
          .filter((form) => (form.document_name != 'error') && (
            (form as TaxDocument<IRSW2Form>).uploaded_file.id === undefined ||
            (form as TaxDocument<IRSW2Form>).uploaded_file.id === null
          )),
        ...action.payload.tax_document.map((w2) => {
          return {
            document_name: FormTypes.w2,
            uploaded_file: w2,
            resource_uri: "/forms/IRSW2",
            form_data: {
              ...w2,
              employer_tax_id: String(w2.employer_tax_id)?.substring(0, 2) + '-' + String(w2.employer_tax_id)?.substring(2),
            },
            warnings: {},
          };
        })
      ];
      state.selectedFormIndex = state.irsW2Forms.length - 1;
    },
    fetchW2FormDetailsError: (state, action: PayloadAction<{ message: string }>) => {
      state.errorMessage = action.payload.message;
    },
    uploadW2Request: (state, action: PayloadAction<File>) => {
      state.errorMessage = "";
    },
    uploadW2Success: (
      state, 
      action: PayloadAction<TaxDocument<IRSW2Form>>
    ) => {
      const form = action.payload;
      form.form_data.document_owner = DocumentOwner.Self;
      if (form.form_data.is_third_party_sick_pay !== null) {
        // Cast string bools as actual bools
        form.form_data.is_third_party_sick_pay = form.form_data.is_third_party_sick_pay === 'true';
      }

      state.irsW2Forms.push(form);
      state.irsW2Forms = state.irsW2Forms.filter((form) => form.document_name !== 'error');
      state.failureCount = 0;
      state.showLocalWithholdingsUploadPrompt = detectMissingLocalWithholdings(form);
      state.selectedFormIndex = state.irsW2Forms.length - 1;
      const hasInvalidLocality = form.form_data.local_withholdings?.some((locality) => {
        const name = locality.locality_name?.toLowerCase()?.trim();
        return name && knownBadLocalities.includes(name);
      });
      if (hasInvalidLocality) {
        state.showInvalidLocalityPrompt = true;
      }
      var heightPx = (form.form_data.local_withholdings?.length ?? 0) * 125;
      setTimeout(() => {
        window.scrollTo({
          top: isMobile ? document.body.clientHeight - window.innerHeight - 900 - heightPx : 100,
          behavior: 'smooth'
        });
      }, 100);
    },
    uploadW2Error: (state, action: PayloadAction<string>) => {
      state.irsW2Forms.push({ document_name: 'error', message: action.payload });
      state.failureCount += 1;
    },
    deleteW2Request: (state, action: PayloadAction<number>) => {},
    deleteW2Success: (state, action: PayloadAction<number>) => {
      state.irsW2Forms = state.irsW2Forms.filter(
        (_, index) => index !== action.payload
      );
      state.uploadedW2s = state.uploadedW2s?.filter(
        (_, index) => index !== action.payload
      ) ?? null;
      if (state.selectedFormIndex === action.payload) {
        state.selectedFormIndex = -1;
      }
      delete state.editMode[action.payload];
      const returnW2 = state.tax_return?.IRSW2;
      if (returnW2) {
        returnW2.messages = null;
      }
    },
    deleteW2Error: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    confirmAllW2Request: (state, action: PayloadAction<ConfirmW2Params>) => {},
    confirmW2Success: (state, {payload}: PayloadAction<{index: number, document: UploadedW2Document}>) => {
      state.uploadedW2s?.push(payload.document);
      (state.irsW2Forms[payload.index] as TaxDocument<IRSW2Form>)
        .uploaded_file.id = payload.document.tax_document.id;
    },
    updateW2ValueChange: (
      state,
      action: PayloadAction<{ index: number; key: string; value: any; transIdx?: number }>
    ) => {
      let formData;
      if (action.payload.transIdx !== undefined) {
        formData = (state.irsW2Forms[action.payload.index] as TaxDocument<IRSW2Form>).form_data;
        (formData.local_withholdings![action.payload.transIdx] as any)[action.payload.key] = 
          action.payload.value;
        formData.local_withholdings = [...formData.local_withholdings!];
      } else {
        const splits = action.payload.key.split('.');
        formData = (state.irsW2Forms[action.payload.index] as TaxDocument<any>)
          .form_data;
        const _value = action.payload.value;
        if (splits.length === 2) {
          formData[splits[0]][splits[1]] = omitDollar(_value);
        } else {
          formData[action.payload.key] = (typeof _value === 'string')
            ? omitDollar(_value as string) : _value;
        }
      }

      (state.irsW2Forms[action.payload.index] as TaxDocument<IRSW2Form>).form_data = {
        ...formData,
      };
      const returnW2 = state.tax_return?.IRSW2;
      if (returnW2) {
        returnW2.messages = null;
      }
    },
    // START SCHEDULE FORMS
    fetchSchedule1FormDetails: (_) => {},
    fetchSchedule1FormDetailsSuccess: (
      state,
      action: PayloadAction<UploadedSchedule1DocumentCollection>
    ) => {
      state.uploadedSchedule1s = action.payload.tax_document.map((sch1) => {
        return {
          id: sch1.id as number,
          tax_document: {
            id: sch1.id,
            file_name: sch1.file_name,
            file_ulid: sch1.file_ulid,
            form_data: sch1,
          }
        }
      });
      state.irsSchedule1Forms = [
        ...state.irsSchedule1Forms
          .filter((form) => !(form instanceof Error) && (
            (form as TaxDocument<IRSSchedule1Form>).uploaded_file.id === undefined ||
            (form as TaxDocument<IRSSchedule1Form>).uploaded_file.id === null
          )),
        ...action.payload.tax_document.map((sch1) => {
          return {
            document_name: FormTypes.schedule1,
            uploaded_file: sch1 as UploadedFile,
            resource_uri: "/forms/IRSSchedule1",
            form_data: sch1,
            warnings: {},
          };
        })
      ];
    },
    fetchSchedule1FormDetailsError: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.errorMessage = action.payload.message;
    },
    uploadSchedule1Request: (state, action: PayloadAction<File>) => {
      state.errorMessage = "";
    },
    uploadSchedule1Error: (state, action: PayloadAction<string>) => {
      state.irsSchedule1Forms.push({ document_name: 'error', message: action.payload });
      state.failureCount++;
    },
    uploadSchedule1Success: (
      state,
      action: PayloadAction<TaxDocument<IRSSchedule1Form>>
    ) => {
      state.irsSchedule1Forms.push(action.payload);
      state.irsSchedule1Forms = state.irsSchedule1Forms.filter((form) => form.document_name !== 'error');
      state.selectedFormIndex = state.irsSchedule1Forms.length - 1;
      state.failureCount = 0;
    },
    deleteSchedule1Request: (state, action: PayloadAction<number>) => {},
    deleteSchedule1Error: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    deleteSchedule1Success: (
      state,
      action: PayloadAction<number>
    ) => {
      state.irsSchedule1Forms = state.irsSchedule1Forms.filter(
        (_, index) => index !== action.payload
      );
      state.uploadedSchedule1s = state.uploadedSchedule1s?.filter(
        (_, index) => index !== action.payload
      ) ?? null;
      if (state.selectedFormIndex === action.payload) {
        state.selectedFormIndex = -1;
      }
      delete state.editMode[action.payload];
      const returnSchedule1 = state.tax_return?.IRSSchedule1;
      if (returnSchedule1) {
        returnSchedule1.messages = null;
      }
    },
    confirmAllSchedule1Request: (_) => {},
    confirmSchedule1Success: (state, {payload}: PayloadAction<{
      index: number, 
      document: UploadedSchedule1Document
    }>) => {
      state.uploadedSchedule1s?.push(payload.document);
      (state.irsSchedule1Forms[payload.index] as TaxDocument<IRSSchedule1Form>)
        .uploaded_file.id = payload.document.tax_document.id;
    },
    updateSchedule1ValueChange: (
      state,
      action: PayloadAction<{ index: number; key: string; value: any;}>
    ) => {
      const splits = action.payload.key.split('.');
      const formData = (state.irsSchedule1Forms[action.payload.index] as TaxDocument<any>)
        .form_data;
      const _value = action.payload.value;
      if (splits.length === 2) {
        formData[splits[0]][splits[1]] = omitDollar(_value);
      } else {
        formData[action.payload.key] = (typeof _value === 'string')
          ? omitDollar(_value as string) : _value;
      }
      (state.irsSchedule1Forms[action.payload.index] as TaxDocument<IRSSchedule1Form>).form_data = {
        ...formData,
      };
      const returnSch1 = state.tax_return?.IRSSchedule1;
      if (returnSch1) {
        returnSch1.messages = null;
      }
    },
    // START SCHEDULE C FORMS
    fetchScheduleCFormDetails: (_) => {},
    fetchScheduleCFormDetailsSuccess: (
      state,
      action: PayloadAction<UploadedScheduleCDocumentCollection>
    ) => {
      state.uploadedScheduleCs = action.payload.tax_document.map((schC) => {
        return {
          id: schC.id as number,
          tax_document: {
            id: schC.id,
            file_name: schC.file_name,
            file_ulid: schC.file_ulid,
            form_data: schC,
          }
        }
      });
      state.irsScheduleCForms = [
        ...state.irsScheduleCForms
          .filter((form) => !(form instanceof Error) && (
            (form as TaxDocument<IRSScheduleCForm>).uploaded_file.id === undefined ||
            (form as TaxDocument<IRSScheduleCForm>).uploaded_file.id === null
          )),
        ...action.payload.tax_document.map((schC) => {
          return {
            document_name: FormTypes.scheduleC,
            uploaded_file: schC as UploadedFile,
            resource_uri: "/forms/IRSScheduleC",
            form_data: schC,
            warnings: {},
          };
        })
      ];
    },
    fetchScheduleCFormDetailsError: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.errorMessage = action.payload.message;
    },
    uploadScheduleCRequest: (state, action: PayloadAction<File>) => {
      state.errorMessage = "";
    },
    uploadScheduleCError: (state, action: PayloadAction<string>) => {
      state.irsScheduleCForms.push({ document_name: 'error', message: action.payload });
      state.failureCount++;
    },
    uploadScheduleCSuccess: (
      state,
      action: PayloadAction<TaxDocument<IRSScheduleCForm>>
    ) => {
      state.irsScheduleCForms.push(action.payload);
      state.irsScheduleCForms = state.irsScheduleCForms.filter((form) => form.document_name !== 'error');
      state.selectedFormIndex = state.irsScheduleCForms.length - 1;
      state.failureCount = 0;
    },
    deleteScheduleCRequest: (state, action: PayloadAction<number>) => {},
    deleteScheduleCError: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    deleteScheduleCSuccess: (
      state,
      action: PayloadAction<number>
    ) => {
      state.irsScheduleCForms = state.irsScheduleCForms.filter(
        (_, index) => index !== action.payload
      );
      state.uploadedScheduleCs = state.uploadedScheduleCs?.filter(
        (_, index) => index !== action.payload
      ) ?? null;
      if (state.selectedFormIndex === action.payload) {
        state.selectedFormIndex = -1;
      }
      delete state.editMode[action.payload];
      const returnScheduleC = state.tax_return?.IRSScheduleC;
      if (returnScheduleC) {
        returnScheduleC.messages = null;
      }
    },
    confirmAllScheduleCRequest: (_) => {},
    confirmScheduleCSuccess: (state, {payload}: PayloadAction<{
      index: number, 
      document: UploadedScheduleCDocument
    }>) => {
      state.uploadedScheduleCs?.push(payload.document);
      (state.irsScheduleCForms[payload.index] as TaxDocument<IRSScheduleCForm>)
        .uploaded_file.id = payload.document.tax_document.id;
    },
    updateScheduleCValueChange: (
      state,
      action: PayloadAction<{ index: number; key: string; value: any;}>
    ) => {
      const splits = action.payload.key.split('.');
      const formData = (state.irsScheduleCForms[action.payload.index] as TaxDocument<any>)
        .form_data;
      const _value = action.payload.value;
      if (splits.length === 2) {
        formData[splits[0]][splits[1]] = omitDollar(_value);
      } else {
        formData[action.payload.key] = (typeof _value === 'string')
          ? omitDollar(_value as string) : _value;
      }
      (state.irsScheduleCForms[action.payload.index] as TaxDocument<IRSScheduleCForm>).form_data = {
        ...formData,
      };
      const returnSchC = state.tax_return?.IRSScheduleC;
      if (returnSchC) {
        returnSchC.messages = null;
      }
    },
  }
});

export default taxSlice.reducer;



